import { YOUTUBE_AUTOMATION_STAGE_STATUSES } from './youtubeAutomationStageStatuses'
import * as youtubeUrls from './youtubeUrls'
import { YoutubeSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { StageType, YoutubeRecommendationKey } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import {
  getWaitForElementToAppearPromise,
  wait,
} from '../interruptiblePromises'
import { evaluateXpath } from '../interruptibleXpath'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

// Settings properties
const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) =>
    value === 'Enable' ? 'Disabled' : 'Enabled',
  )

const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => value === 'Enable')

const getCantUndoFn = () => defaultCantUndoFn
const getIsDisabledFn = () => defaultIsDisabledFn
const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

// DOM Helper
async function getQuickPurchaseButton(): Promise<HTMLButtonElement> {
  const buttonXpath = '//*[@id="connect-button"]//button'
  const button = await getWaitForElementToAppearPromise<HTMLButtonElement>(
    document,
    (node) => evaluateXpath<HTMLButtonElement>(node, buttonXpath)[0],
    3000,
  )

  return button
}

// Stage definitions
enum StageKey {
  GATHER_SETTINGS_QUICK_PURCHASE = 'GATHER_SETTINGS_QUICK_PURCHASE',
  UPDATE_SETTINGS_QUICK_PURCHASE = 'UPDATE_SETTINGS_QUICK_PURCHASE',
  UPDATE_SETTINGS_QUICK_PURCHASE_REFRESH = 'UPDATE_SETTINGS_QUICK_PURCHASE_REFRESH',
}

const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_SETTINGS_QUICK_PURCHASE,
    status: YOUTUBE_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: youtubeUrls.billingUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredInitialValues } = userContext

      const quickPurchaseButton = await getQuickPurchaseButton()

      await updateStoredInitialValues(
        {
          [YoutubeSettingKey.QUICK_PURCHASE]: quickPurchaseButton.ariaLabel,
        },
        YoutubeRecommendationKey.YOUTUBE_PURCHASE,
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_SETTINGS_QUICK_PURCHASE,
    status: YOUTUBE_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: youtubeUrls.billingUrl,
    action: async (machineContext, userContext) => {
      const { isDry, settingsSelected } = userContext

      if (settingsSelected[YoutubeSettingKey.QUICK_PURCHASE]) {
        const quickPurchaseButton = await getQuickPurchaseButton()

        if (
          !isDry &&
          quickPurchaseButton &&
          quickPurchaseButton.ariaLabel === 'Disable'
        ) {
          quickPurchaseButton.click()
        }
      }
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_SETTINGS_QUICK_PURCHASE_REFRESH,
    status: YOUTUBE_AUTOMATION_STAGE_STATUSES.CHANGING_PAGES,
    url: youtubeUrls.billingUrl,
    action: async () => {
      await wait(1000)
      window.location.reload()
    },
  },
]

const undoStageDefs: StageDef[] = []

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
